export type RunsPaginationSearch = {
  page: number;
  pageSize: number;
};

export type TestRunsSearch = RunsPaginationSearch & {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** `RunTarget.key` values — see `packages/types/src/run-target.ts`. */
  targets?: string[];
  statuses?: string[];
  /** User ids, plus the literal `'system'` for runs with no session user. */
  triggeredBy?: string[];
  startedAfter?: string;
  startedBefore?: string;
};

const parseSearchNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return undefined;
};

const parseSearchString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const parseSearchIsoDate = (value: unknown): string | undefined => {
  const str = parseSearchString(value);
  if (!str) return undefined;
  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? undefined : str;
};

/** Mirrors `parseSearchArray` in `router.tsx` — kept local so this module has no dependency on it. */
const parseSearchArray = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) {
    return value
      .filter((v) => typeof v === 'string' || typeof v === 'number')
      .map((v) => String(v).trim())
      .filter((v) => v.length > 0);
  }
  if (typeof value === 'number') return [String(value)];
  if (typeof value === 'string') {
    if (value.trim().length === 0) return [];
    return value
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  }
  return undefined;
};

export const validateRunsPaginationSearch = (
  search: Record<string, unknown>
): RunsPaginationSearch => ({
  page: parseSearchNumber(search.page) ?? 1,
  pageSize: parseSearchNumber(search.pageSize) ?? 20,
});

export const validateQaRunsSearch = (search: Record<string, unknown>): RunsPaginationSearch =>
  validateRunsPaginationSearch(search);

export const validateTestRunsSearch = (search: Record<string, unknown>): TestRunsSearch => ({
  ...validateRunsPaginationSearch(search),
  sortBy: parseSearchString(search.sortBy),
  sortOrder: parseSearchString(search.sortOrder) as 'asc' | 'desc' | undefined,
  targets: parseSearchArray(search.targets),
  statuses: parseSearchArray(search.statuses),
  triggeredBy: parseSearchArray(search.triggeredBy),
  startedAfter: parseSearchIsoDate(search.startedAfter),
  startedBefore: parseSearchIsoDate(search.startedBefore),
});
